import React, { useEffect, useRef, useState } from 'react';

interface GoogleRecaptchaProps {
  onLoad?: () => void;
  onError?: (error: string) => void;
}

interface WindowWithGrecaptcha extends Window {
  grecaptcha?: {
    ready: (callback: () => void) => void;
    execute: (siteKey: string, options: { action: string }) => Promise<string>;
    render: (container: string | HTMLElement, options: any) => number;
  };
}

const SITE_KEY = '6LfcvUgjAAAAAJbHh7QcDTNFh8A4I3vNehz-z6FA';

export const useGoogleRecaptcha = () => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Check if reCAPTCHA is fully ready (not just script loaded)
  useEffect(() => {
    const checkRecaptchaReady = () => {
      const windowWithGrecaptcha = window as WindowWithGrecaptcha;
      if (windowWithGrecaptcha.grecaptcha && 
          typeof windowWithGrecaptcha.grecaptcha.ready === 'function' &&
          typeof windowWithGrecaptcha.grecaptcha.execute === 'function') {
        
        // Test if reCAPTCHA is actually working
        try {
          windowWithGrecaptcha.grecaptcha.ready(() => {
            console.log('✅ reCAPTCHA fully ready and functional');
            setIsLoaded(true);
          });
          return true;
        } catch (err) {
          console.log('⏳ reCAPTCHA script loaded but not ready yet');
          return false;
        }
      }
      return false;
    };

    // Check immediately
    if (checkRecaptchaReady()) return;

    // Check periodically until fully ready
    const interval = setInterval(() => {
      if (checkRecaptchaReady()) {
        clearInterval(interval);
      }
    }, 200); // Increased interval to 200ms
    
    return () => clearInterval(interval);
  }, []);

  const executeRecaptcha = async (action: string): Promise<string> => {
    if (!isLoaded) {
      throw new Error('reCAPTCHA not fully loaded yet');
    }

    setIsLoading(true);
    setError(null);

    try {
      const windowWithGrecaptcha = window as WindowWithGrecaptcha;
      
      if (!windowWithGrecaptcha.grecaptcha) {
        throw new Error('reCAPTCHA not available');
      }

      return new Promise((resolve, reject) => {
        windowWithGrecaptcha.grecaptcha.ready(async () => {
          try {
            const token = await windowWithGrecaptcha.grecaptcha.execute(SITE_KEY, { action });
            resolve(token);
          } catch (err) {
            reject(err);
          }
        });
      });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setError(errorMessage);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    isLoaded,
    isLoading,
    error,
    executeRecaptcha
  };
};

export default function GoogleRecaptcha({ onLoad, onError }: GoogleRecaptchaProps) {
  const scriptRef = useRef<HTMLScriptElement | null>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);

  useEffect(() => {
    // Check if reCAPTCHA is already available and functional
    const checkRecaptchaReady = () => {
      const windowWithGrecaptcha = window as WindowWithGrecaptcha;
      if (windowWithGrecaptcha.grecaptcha && 
          typeof windowWithGrecaptcha.grecaptcha.ready === 'function' &&
          typeof windowWithGrecaptcha.grecaptcha.execute === 'function') {
        
        // Test if reCAPTCHA is actually working
        try {
          windowWithGrecaptcha.grecaptcha.ready(() => {
            console.log('✅ reCAPTCHA already available and functional');
            setIsLoaded(true);
            onLoad?.();
          });
          return true;
        } catch (err) {
          console.log('⏳ reCAPTCHA available but not ready yet');
          return false;
        }
      }
      return false;
    };

    // Check immediately
    if (checkRecaptchaReady()) return;

    // Only load if not already loaded by RecaptchaLoader
    if (isLoaded || document.querySelector('script[src*="google.com/recaptcha"]')) {
      // Even if script exists, we need to verify it's functional
      if (checkRecaptchaReady()) return;
    }

    // Prevent multiple script loading - let RecaptchaLoader handle it
    if ((window as any).grecaptcha) {
      if (checkRecaptchaReady()) return;
    }

    // Don't load script here - let RecaptchaLoader handle it
    console.log('📜 reCAPTCHA script loading handled by RecaptchaLoader');
    
    // Just wait for it to become available
    const checkInterval = setInterval(() => {
      if (checkRecaptchaReady()) {
        clearInterval(checkInterval);
      }
    }, 100);
    
    // Fallback timeout
    setTimeout(() => {
      if (!isLoaded) {
        console.error('❌ reCAPTCHA failed to initialize after 5 seconds');
        onError?.('reCAPTCHA initialization timeout');
      }
    }, 5000);

    return () => {
      clearInterval(checkInterval);
    };
  }, [isLoaded, onLoad, onError]);

  return null; // This component doesn't render anything visible
}
